import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';

import i18n from 'i18n-js';
import {
    ListItem,
    ListItemText,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Checkbox,
    FormControlLabel,
    Chip,
} from '@material-ui/core';
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline';
import SettingsBackupRestoreIcon from '@material-ui/icons/SettingsBackupRestore';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { FilterNone } from '@material-ui/icons';
import clsx from 'clsx';

import { default as restaurantsStore } from 'store/restaurantsStore';
import { ApplicationState } from 'store';
import logo from 'assets/restaurant-logo.svg';
import { RestaurantFilterModel } from 'api/models';
import { CellWithAction } from './CellWithAction';
import {
    BasicThreeDotsMenu,
    SearchInput,
    PopupConfirmation,
    NothingFoundMessage,
    SkeletonBlock,
    TableSkeleton,
} from 'components/shared';
import { useFirstLoad } from 'hooks/useFirstLoad';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { RestaurantData } from 'store/restaurantsStore/reducer';
import { HeadCell } from 'constants/interfaces';
import { useStyles } from '../styles';

export interface RestaurantsTableProps {
    isCoolBoxChecked: boolean;
    handleCoolboxCheckbox: () => void;
}

export default function RestaurantsTable(props: RestaurantsTableProps) {
    const dispatch = useThunkDispatch();
    const [isFirstLoading, settleFirstLoad] = useFirstLoad();
    const history = useHistory();
    const classes = useStyles();
    const restaurantsState = useSelector((state: ApplicationState) => state.restaurants);
    const [rowsPerPage] = useState(30);
    const [orderBy, setOrderBy] = useState('name');
    const [asc, setAsc] = useState(true);
    const [selectedRestaurant, setSelectedRestaurant] = useState<RestaurantData | null>(null);
    const page = useRef(0);
    const loaderRef = useRef<HTMLTableRowElement | null>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);

    const headCells: HeadCell[] = [
        { id: 'name', label: 'Restaurant name' },
        { id: 'address', label: 'Address' },
        { id: 'zip', label: 'ZIP Code' },
        { id: 'city', label: 'City' },
    ];
    const defaultFilter: RestaurantFilterModel = {
        name: '',
        addressLine: '',
        zip: '',
        city: '',
        showCoolBoxes: props.isCoolBoxChecked,
    };
    const [filter, setFilter] = useState(defaultFilter);

    function getRestaurants(
        skipPaging?: boolean,
        sortBy?: string,
        newFilter?: RestaurantFilterModel
    ) {
        return dispatch(
            restaurantsStore.actionCreators.getRestaurants(
                newFilter
                    ? { ...newFilter, showCoolBoxes: props.isCoolBoxChecked }
                    : { ...filter, showCoolBoxes: props.isCoolBoxChecked },
                page.current,
                rowsPerPage,
                !asc,
                sortBy || orderBy,
                skipPaging
            )
        );
    }

    const loadMore = useCallback(() => {
        page.current++;
        getRestaurants(true);
    }, [getRestaurants]);

    useEffect(() => {
        page.current = 0;
        getRestaurants().finally(settleFirstLoad);
    }, [props.isCoolBoxChecked, filter, orderBy, asc]);

    useEffect(() => {
        if (observerRef.current) {
            observerRef.current.disconnect();
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    loadMore();
                }
            },
            { rootMargin: '200px' }
        );
        observerRef.current = observer;

        let retryCount = 0;
        const maxRetries = 10;
        function tryObserve() {
            if (loaderRef.current instanceof Element) {
                observer.observe(loaderRef.current);
            } else if (retryCount < maxRetries) {
                retryCount++;
                setTimeout(tryObserve, 300);
            }
        }
        tryObserve();

        return () => {
            observer.disconnect();
        };
    }, [props.isCoolBoxChecked, filter, orderBy, asc]);

    const handleFilterChange = () => {
        page.current = 0;
        getRestaurants();
    };

    function handleOrder(id: string) {
        page.current = 0;
        if (orderBy === id) {
            setAsc(!asc);
            getRestaurants(false, orderBy);
        } else {
            getRestaurants(false, id);
            setOrderBy(id);
        }
    }

    function clearFilterField(value: keyof RestaurantFilterModel) {
        if (filter.hasOwnProperty(value)) {
            page.current = 0;
            const newFilter = { ...filter, [value]: '' };
            setFilter(newFilter);
            getRestaurants(undefined, undefined, newFilter);
        }
    }

    function switchRestaurantStatus() {
        selectedRestaurant &&
            dispatch(restaurantsStore.actionCreators.switchRestaurantStatus(selectedRestaurant));
        setSelectedRestaurant(null);
    }

    function prepareContextMenuItems(rest: RestaurantData) {
        let result = [
            <div
                className={classes.threeDotsMenuItemContainer}
                onClick={() => history.push(`/restaurants/${rest.id}`)}
            >
                <ErrorOutlineIcon className={classes.threeDotsItemIcon} />
                <ListItemText primary={i18n.t('common.details')} />
            </div>,
        ];

        if (!rest.isDisabled) {
            result.push(
                <div
                    className={classes.threeDotsMenuItemContainer}
                    onClick={() => history.push(`/restaurants/${rest.id}/copy`)}
                >
                    <FilterNone className={classes.threeDotsItemIcon} />
                    <ListItemText primary={i18n.t('restaurant.copyRestaurant')} />
                </div>
            );

            result.push(
                <div
                    className={classes.threeDotsMenuItemContainer}
                    onClick={() => setSelectedRestaurant(rest)}
                >
                    <RemoveCircleOutlineIcon
                        color="disabled"
                        className={classes.threeDotsItemIcon}
                    />
                    <ListItemText primary={i18n.t('common.disable')} />
                </div>
            );
        } else {
            result.push(
                <div
                    className={classes.threeDotsMenuItemContainer}
                    onClick={() => setSelectedRestaurant(rest)}
                >
                    <SettingsBackupRestoreIcon
                        color="disabled"
                        className={classes.threeDotsItemIcon}
                    />
                    <ListItemText primary={i18n.t('button.restore')} />
                </div>
            );
        }

        return result;
    }

    return (
        <>
            <FormControlLabel
                className={clsx(classes.tableFilers)}
                control={
                    <Checkbox
                        onChange={props.handleCoolboxCheckbox}
                        checked={props.isCoolBoxChecked}
                    />
                }
                label={i18n.t('restaurant.jamesCoolBox')}
            />
            <TableContainer style={{ flex: 1 }}>
                <Table stickyHeader className={classes.table} aria-label="simple table">
                    <TableHead className={classes.tableHead}>
                        <TableRow key="table-head">
                            <TableCell sortDirection="asc">
                                <TableSortLabel
                                    hideSortIcon={false}
                                    active={orderBy === headCells[0].id}
                                    direction={asc ? 'asc' : 'desc'}
                                    onClick={() => {
                                        handleOrder(headCells[0].id);
                                    }}
                                >
                                    <SearchInput
                                        value={filter.name}
                                        name={i18n.t('form.restaurantName')}
                                        onChange={(value: string) =>
                                            setFilter({ ...filter, name: value })
                                        }
                                        onEnter={handleFilterChange}
                                        onReset={() => clearFilterField('name')}
                                        trimValue={false}
                                    />
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection="asc">
                                <TableSortLabel
                                    active={orderBy === headCells[1].id}
                                    direction={asc ? 'asc' : 'desc'}
                                    onClick={() => handleOrder(headCells[1].id)}
                                >
                                    <SearchInput
                                        value={filter.addressLine}
                                        name={i18n.t('form.restaurantAddress')}
                                        onChange={(value: string) =>
                                            setFilter({ ...filter, addressLine: value })
                                        }
                                        onEnter={handleFilterChange}
                                        onReset={() => clearFilterField('addressLine')}
                                    />
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection="asc" width={150}>
                                <TableSortLabel
                                    active={orderBy === headCells[2].id}
                                    direction={asc ? 'asc' : 'desc'}
                                    onClick={() => handleOrder(headCells[2].id)}
                                >
                                    <SearchInput
                                        value={filter.zip}
                                        name={i18n.t('form.zip')}
                                        onChange={(value: string) =>
                                            setFilter({ ...filter, zip: value })
                                        }
                                        onEnter={handleFilterChange}
                                        onReset={() => clearFilterField('zip')}
                                    />
                                </TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection="asc" width={150}>
                                <TableSortLabel
                                    active={orderBy === headCells[3].id}
                                    direction={asc ? 'asc' : 'desc'}
                                    onClick={() => handleOrder(headCells[3].id)}
                                >
                                    <SearchInput
                                        value={filter.city}
                                        name={i18n.t('form.city')}
                                        onChange={(value: string) =>
                                            setFilter({ ...filter, city: value })
                                        }
                                        onEnter={handleFilterChange}
                                        onReset={() => clearFilterField('city')}
                                    />
                                </TableSortLabel>
                            </TableCell>
                            <TableCell width={20}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {restaurantsState?.restaurants?.length > 0 ? (
                            <>
                                {restaurantsState?.restaurants?.map((row) => (
                                    <TableRow
                                        key={row.id}
                                        className={clsx({
                                            [classes.disabledRow]: row.isDisabled,
                                        })}
                                    >
                                        <CellWithAction
                                            id={row.id}
                                            content={
                                                <ListItem alignItems="center">
                                                    <div
                                                        className={classes.logoContainer}
                                                        style={{
                                                            backgroundImage: `url(${
                                                                row.logoUrl ? row.logoUrl : logo
                                                            })`,
                                                        }}
                                                    ></div>

                                                    <ListItemText
                                                        primary={row.name}
                                                        style={{ flexGrow: 'unset' }}
                                                    />
                                                    {row.isCoolBox && (
                                                        <Chip
                                                            className={classes.smartFridgeLabel}
                                                            label={i18n.t(
                                                                'restaurant.jamesCoolBox'
                                                            )}
                                                            color="primary"
                                                        />
                                                    )}
                                                </ListItem>
                                            }
                                        />
                                        <CellWithAction id={row.id} content={row.addressLine} />
                                        <CellWithAction id={row.id} content={row.zip} />
                                        <CellWithAction id={row.id} content={row.city} />
                                        <TableCell align="right">
                                            <BasicThreeDotsMenu
                                                items={prepareContextMenuItems(row)}
                                            />
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {restaurantsState?.restaurants?.length <
                                    restaurantsState?.count && (
                                    <tr
                                        ref={loaderRef}
                                        style={{ height: 20, background: 'transparent' }}
                                    >
                                        <td colSpan={6} style={{ textAlign: 'center' }}>
                                            {i18n.t('common.loading')}{' '}
                                        </td>
                                    </tr>
                                )}
                            </>
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} style={{ height: 550 }}>
                                    <NothingFoundMessage />
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>

                <PopupConfirmation
                    open={!!selectedRestaurant}
                    close={() => setSelectedRestaurant(null)}
                    activeBtn={
                        selectedRestaurant?.isDisabled
                            ? i18n.t('button.restore')
                            : i18n.t('common.disable')
                    }
                    description={
                        selectedRestaurant?.isDisabled
                            ? i18n.t('confirmation.sureToRestoreRestaurant')
                            : i18n.t('confirmation.sureToDisableRestaurant')
                    }
                    title={
                        selectedRestaurant?.isDisabled
                            ? i18n.t('restaurant.restoreRestaurant')
                            : i18n.t('restaurant.disableRestaurant')
                    }
                    action={switchRestaurantStatus}
                />
            </TableContainer>
            <TableSkeleton
                isLoading={isFirstLoading}
                columnWidths={[undefined, undefined, 100, undefined, 40]}
                headerHeight={64}
            >
                <SkeletonBlock height={24} width={180} />
            </TableSkeleton>
        </>
    );
}
